import React from 'react';

interface ReworkData {
  total_rework_count: number;
  rework_rate_percent: number;
  total_rework_cost: number;
  avg_rework_cost: number;
  rework_by_step: Array<{
    step_name: string;
    count: number;
    total_cost: number;
  }>;
}

interface ReworkStatsProps {
  data: ReworkData | null;
  loading: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export function ReworkStats({ data, loading }: ReworkStatsProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rework Analysis</h3>
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rework Analysis</h3>
        <div className="h-48 flex items-center justify-center text-gray-500">
          No rework data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Rework Analysis</h3>
        {data.total_rework_count > 0 && (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            data.rework_rate_percent > 5
              ? 'bg-red-100 text-red-800'
              : data.rework_rate_percent > 2
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-green-100 text-green-800'
          }`}>
            {data.rework_rate_percent.toFixed(1)}% Rate
          </span>
        )}
      </div>

      {data.total_rework_count === 0 ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-600 font-medium">No rework in this period</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-orange-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">Total Rework</p>
              <p className="text-xl font-bold text-orange-600">{data.total_rework_count}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">Total Cost</p>
              <p className="text-xl font-bold text-orange-600">{formatCurrency(data.total_rework_cost)}</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-600">Avg Cost per Rework</p>
            <p className="text-lg font-semibold text-gray-900">{formatCurrency(data.avg_rework_cost)}</p>
          </div>

          {data.rework_by_step.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Rework by Workstation</h4>
              <div className="space-y-2">
                {data.rework_by_step.slice(0, 5).map((step, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{step.step_name}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900">{step.count}</span>
                      <span className="text-gray-500">{formatCurrency(step.total_cost)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ReworkStats;
